import React, { useState, useEffect } from "react";
import { Button, Column, Icon, Row, Select, SelectOption, Text } from "@amzn/meridian";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { setStreamingConfig } from "src/store/inspectionCaptureSlice";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import chevronDownSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-down-small";
import chevronUpSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-up-small";
import "./streamingDevControls.scss";

interface StreamingDevControlsProps {
  streamingService: any; // ImageStreamingWebSocketService instance
}

const StreamingDevControls: React.FC<StreamingDevControlsProps> = ({ streamingService }) => {
  const dispatch = useAppDispatch();
  const { streamingMetrics, streamingConnected, streamingConfig, screenWidth, screenHeight } = useAppSelector((state) => state.inspectionCapture);
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [resolution, setResolution] = useState(`${streamingConfig.maxWidth}x${streamingConfig.maxHeight}`);
  const [quality, setQuality] = useState(streamingConfig.quality);
  const [interval, setInterval] = useState(streamingConfig.interval);
  const [resolutionScale, setResolutionScale] = useState(streamingConfig.resolutionScale);
  const [sessionDuration, setSessionDuration] = useState("0s");

  // Update session duration display
  useEffect(() => {
    if (!streamingMetrics.sessionStartTime) {
      setSessionDuration("0s");
      return;
    }

    const updateDuration = () => {
      const elapsed = Date.now() - (streamingMetrics.sessionStartTime || 0);
      const seconds = Math.floor(elapsed / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);

      if (hours > 0) {
        setSessionDuration(`${hours}h ${minutes % 60}m`);
      } else if (minutes > 0) {
        setSessionDuration(`${minutes}m ${seconds % 60}s`);
      } else {
        setSessionDuration(`${seconds}s`);
      }
    };

    updateDuration();
    const intervalId = window.setInterval(updateDuration, 1000);

    return () => window.clearInterval(intervalId);
  }, [streamingMetrics.sessionStartTime]);

  const handleResolutionChange = (value: string) => {
    setResolution(value);
    const [width, height] = value.split("x").map(Number);
    
    if (streamingService) {
      streamingService.updateOptions({
        maxWidth: width,
        maxHeight: height,
      });
    }
  };

  const handleQualityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuality = parseFloat(e.target.value);
    setQuality(newQuality);
    
    if (streamingService) {
      streamingService.updateOptions({
        quality: newQuality,
      });
    }
  };

  const handleIntervalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newInterval = parseInt(e.target.value);
    setInterval(newInterval);
    
    if (streamingService) {
      streamingService.updateOptions({
        interval: newInterval,
      });
    }
  };

  if (!isExpanded) {
    return (
      <div className="streaming-dev-controls collapsed">
        <Button
          type="icon"
          onClick={() => setIsExpanded(true)}
          className="expand-button"
        >
          <Icon tokens={chevronDownSmallTokens} />
        </Button>
        <Text type="b200" className="collapsed-text">Dev Controls</Text>
      </div>
    );
  }

  return (
    <div className="streaming-dev-controls expanded">
      <Row alignmentHorizontal="justify" alignmentVertical="center" className="header">
        <Text type="b300">Dev Mode - Streaming Controls</Text>
        <Button
          type="icon"
          onClick={() => setIsExpanded(false)}
          className="collapse-button"
        >
          <Icon tokens={chevronUpSmallTokens} />
        </Button>
      </Row>

      <Column spacing="200" className="controls-section">
        <Text type="b200">Stream Settings</Text>
        
        <Column spacing="100">
          <Row alignmentHorizontal="justify">
            <Text type="b100">Resolution Scale (Low-Res)</Text>
            <Text type="b100">{(resolutionScale * 100).toFixed(0)}% of screen</Text>
          </Row>
          <Row spacing="200" alignmentVertical="center">
            <input
              type="range"
              min="0.3"
              max="1.0"
              step="0.1"
              value={resolutionScale}
              onChange={(e) => {
                const newScale = parseFloat(e.target.value);
                setResolutionScale(newScale);
                dispatch(setStreamingConfig({ resolutionScale: newScale }));
              }}
              className="slider"
              disabled={!streamingConnected}
            />
          </Row>
          <Row spacing="100" alignmentHorizontal="justify" className="scale-options">
            <Button
              type={resolutionScale === 0.3 ? "primary" : "tertiary"}
              size="small"
              onClick={() => {
                setResolutionScale(0.3);
                dispatch(setStreamingConfig({ resolutionScale: 0.3 }));
              }}
            >
              30%
            </Button>
            <Button
              type={resolutionScale === 0.5 ? "primary" : "tertiary"}
              size="small"
              onClick={() => {
                setResolutionScale(0.5);
                dispatch(setStreamingConfig({ resolutionScale: 0.5 }));
              }}
            >
              50%
            </Button>
            <Button
              type={resolutionScale === 0.8 ? "primary" : "tertiary"}
              size="small"
              onClick={() => {
                setResolutionScale(0.8);
                dispatch(setStreamingConfig({ resolutionScale: 0.8 }));
              }}
            >
              80%
            </Button>
            <Button
              type={resolutionScale === 1.0 ? "primary" : "tertiary"}
              size="small"
              onClick={() => {
                setResolutionScale(1.0);
                dispatch(setStreamingConfig({ resolutionScale: 1.0 }));
              }}
            >
              100%
            </Button>
          </Row>
          <Text type="b100" className="helper-text">
            Current: {Math.floor((screenWidth || 2560) * resolutionScale)}x{Math.floor((screenHeight || 1440) * resolutionScale)}
          </Text>
        </Column>

        <Column spacing="100">
          <Row alignmentHorizontal="justify">
            <Text type="b100">Quality</Text>
            <Text type="b100">{(quality * 100).toFixed(0)}%</Text>
          </Row>
          <input
            type="range"
            min="0.3"
            max="1.0"
            step="0.1"
            value={quality}
            onChange={handleQualityChange}
            className="slider"
            disabled={!streamingConnected}
          />
        </Column>

        <Column spacing="100">
          <Row alignmentHorizontal="justify">
            <Text type="b100">Frame Interval</Text>
            <Text type="b100">{interval}ms</Text>
          </Row>
          <input
            type="range"
            min="500"
            max="2000"
            step="100"
            value={interval}
            onChange={handleIntervalChange}
            className="slider"
            disabled={!streamingConnected}
          />
        </Column>
      </Column>

      <Column spacing="200" className="metrics-section">
        <Text type="b200">Real-Time Metrics</Text>
        
        <Row className="metric-row" alignmentHorizontal="justify">
          <Text type="b100">Current Image Size:</Text>
          <Text type="b100" className="metric-value">
            {streamingMetrics.currentImageSize} KB
          </Text>
        </Row>

        <Row className="metric-row" alignmentHorizontal="justify">
          <Text type="b100">Current Latency:</Text>
          <Text type="b100" className="metric-value">
            {streamingMetrics.currentLatency} ms
          </Text>
        </Row>

        <Row className="metric-row" alignmentHorizontal="justify">
          <Text type="b100">Images Sent:</Text>
          <Text type="b100" className="metric-value">
            {streamingMetrics.imagesSent}
          </Text>
        </Row>

        <Row className="metric-row" alignmentHorizontal="justify">
          <Text type="b100">Avg Latency:</Text>
          <Text type="b100" className="metric-value">
            {streamingMetrics.averageLatency} ms
          </Text>
        </Row>

        <Row className="metric-row" alignmentHorizontal="justify">
          <Text type="b100">Session Duration:</Text>
          <Text type="b100" className="metric-value">
            {sessionDuration}
          </Text>
        </Row>
      </Column>
    </div>
  );
};

export default StreamingDevControls;
